import { Link } from 'react-router-dom';
import { useContext, useMemo } from 'react';
import { useCart } from '@/features/cart/context/CartContext';
import { AuthContext } from '@/features/auth/context/AuthContext';
import { resolveUnitPrice } from '@/lib/pricing';
import '../cart.css';
import { fmt } from '@/lib/format';
import { getPackageDisplayName, getProductDisplayName } from '@/lib/packageDisplay';
import WholesaleNotice from '@/features/products/components/WholesaleNotice';
import { Package } from 'lucide-react'; // === [ADDED] ===

export default function CartPage() {
  const {
    cart,
    removeFromCart,
    updateQuantity,
    canShop,
    cartWarning,
  } = useCart();

  const { user } = useContext(AuthContext);

  const total = cart.reduce((sum, item) => {
    const baseUnitPrice = resolveUnitPrice(item, item.quantity || 1);
    const discount = Number(item.discount_percent) || 0;
    const finalUnitPrice = baseUnitPrice * (1 - (discount / 100));
    return sum + (finalUnitPrice * (item.quantity || 1));
  }, 0);

  // === [ADDED] گروه‌بندی اقلام پکیج ===
  const groupedCart = useMemo(() => {
    const groups = [];
    const packages = {};

    cart.forEach(item => {
      if (item.package_name) {
        if (!packages[item.package_name]) {
          packages[item.package_name] = {
            type: 'package',
            packageName: item.package_name,
            items: [],
            totalBase: 0,
            totalFinal: 0,
            firstItemId: item.id
          };
          groups.push(packages[item.package_name]);
        }
        const pkg = packages[item.package_name];
        pkg.items.push(item);

        const basePrice = resolveUnitPrice(item, item.quantity || 1);
        const discount = Number(item.discount_percent) || 0;
        const finalPrice = basePrice * (1 - (discount / 100));

        pkg.totalBase += basePrice * (item.quantity || 1);
        pkg.totalFinal += finalPrice * (item.quantity || 1);
      } else {
        groups.push({ type: 'item', item });
      }
    });
    return groups;
  }, [cart]);
  // ======================================

  const handleRemoveClick = (item) => {
    if (item.package_name) {
      if (window.confirm(`این کالا متعلق به «${getPackageDisplayName(item.package_name)}» است. با حذف آن، تمام اقلام این سبد قرارداد حذف خواهند شد. آیا مطمئن هستید؟`)) {
        removeFromCart(item.id);
      }
    } else {
      removeFromCart(item.id);
    }
  };

  return (
      <div className="container cart-page">
        <h2>سبد خرید</h2>

        {cartWarning && (
          <div
            role="alert"
            style={{
              background: '#fffbeb',
              border: '1px solid #f59e0b',
              borderRadius: '10px',
              padding: '0.75rem 1rem',
              marginBottom: '1rem',
              color: '#92400e',
            }}
          >
            {cartWarning}
          </div>
        )}

        {cart.length === 0 ? (
            <div className="cart-empty">
              <i className="fas fa-shopping-cart" />
              <p>سبد خرید شما خالی است</p>
              <Link to="/products" className="btn btn-primary">
                مشاهده محصولات
              </Link>
            </div>
        ) : (
            <>
              <div className="cart-items">
                {groupedCart.map((group) => {
                  // === [ADDED] رندر کارت پکیج ===
                  if (group.type === 'package') {
                    return (
                        <div key={group.packageName} className="cart-item package-item" style={{ border: '2px solid #10b981', background: '#f0fdf4' }}>
                          <div className="cart-item-info" style={{ flex: 1 }}>
                            <p className="name" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: '#065f46', fontSize: '1.1rem', fontWeight: 'bold' }}>
                              <Package size={20} />
                              {getPackageDisplayName(group.packageName)}
                            </p>
                            <div style={{ fontSize: '0.85rem', color: '#047857', marginTop: '0.5rem', lineHeight: '1.6' }}>
                              <strong>شامل:</strong> {group.items.map(i => `${getProductDisplayName(i.name_fa, i.name_en)} (${i.quantity} عدد)`).join(' ، ')}
                            </div>
                          </div>

                          <div className="cart-item-qty">
                              <span style={{ fontSize: '0.85rem', color: '#047857', fontWeight: 'bold', background: '#d1fae5', padding: '4px 12px', borderRadius: '20px' }}>
                                بسته گروهی
                              </span>
                          </div>

                          <div className="cart-item-subtotal" style={{ minWidth: '150px' }}>
                            <span style={{ textDecoration: 'line-through', color: '#9ca3af', fontSize: '0.85rem', display: 'block' }}>
                              {fmt(group.totalBase)} تومان
                            </span>
                            <span style={{ color: '#059669', fontWeight: 'bold', fontSize: '1.1rem' }}>
                              {fmt(group.totalFinal)} تومان
                            </span>
                          </div>

                          <button
                              type="button"
                              className="cart-item-remove"
                              onClick={() => handleRemoveClick({ id: group.firstItemId, package_name: group.packageName })}
                              title="حذف کل پکیج"
                          >
                            <i className="fas fa-trash" />
                          </button>
                        </div>
                    );
                  }
                  // =============================

                  // رندر کالای عادی
                  const item = group.item;
                  const quantity = item.quantity || 1;
                  const stock = Number(item.stock_quantity);
                  const canIncrease = Number.isFinite(stock) && stock > 0 && quantity < stock;

                  const baseUnitPrice = resolveUnitPrice(item, quantity);
                  const discount = Number(item.discount_percent) || 0;
                  const finalUnitPrice = baseUnitPrice * (1 - (discount / 100));
                  const subtotal = finalUnitPrice * quantity;

                  const isWholesale = baseUnitPrice !== (Number(item.base_price) || 0);
                  const hasDiscount = discount > 0;

                  return (
                      <div key={item.id} className="cart-item">
                        {item.image_url ? (
                            <img
                                className="cart-item-thumb"
                                src={item.image_url.startsWith('/') ? `${item.image_url}` : item.image_url}
                                alt={item.name_fa || 'محصول'}
                            />
                        ) : (
                            <div className="cart-item-thumb-placeholder">بدون تصویر</div>
                        )}

                        <div className="cart-item-info">
                          <p className="name">{item.name_fa || 'محصول'}</p>
                          {item.brand && <p className="brand">برند: {item.brand}</p>}

                          <div className="price" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px', marginTop: '4px' }}>
                            {hasDiscount ? (
                                <>
                                  <span style={{ color: '#059669', fontWeight: 'bold' }}>
                                    {fmt(finalUnitPrice)} تومان
                                    <span style={{ fontSize: '0.8rem', background: '#dcfce7', color: '#166534', padding: '2px 6px', borderRadius: '4px', marginRight: '6px' }}>
                                      {discount}٪ تخفیف
                                    </span>
                                  </span>
                                  <span style={{ fontSize: '0.8rem', textDecoration: 'line-through', color: '#9ca3af' }}>
                                    {fmt(baseUnitPrice)} تومان
                                  </span>
                                </>
                            ) : (
                                <span>{fmt(baseUnitPrice)} تومان</span>
                            )}
                            {isWholesale && <span className="cart-item-wholesale-badge"> (قیمت عمده)</span>}
                          </div>

                          <WholesaleNotice item={item} quantity={quantity} />
                        </div>

                        <div className="cart-item-qty">
                          <button
                              type="button"
                              onClick={() => updateQuantity(item.id, Math.max(1, quantity - 1))}
                              disabled={quantity <= 1}
                          >
                            −
                          </button>
                          <span>{quantity}</span>
                          <button
                              type="button"
                              onClick={() => {
                                if (canIncrease) updateQuantity(item.id, quantity + 1);
                              }}
                              disabled={!canIncrease}
                          >
                            +
                          </button>
                        </div>

                        <div className="cart-item-subtotal">
                          <p style={{ color: hasDiscount ? '#059669' : 'inherit', fontWeight: hasDiscount ? 'bold' : 'normal' }}>
                            {fmt(subtotal)} تومان
                          </p>
                        </div>

                        <button
                            type="button"
                            className="cart-item-remove"
                            onClick={() => handleRemoveClick(item)}
                            title="حذف"
                        >
                          <i className="fas fa-trash" />
                        </button>
                      </div>
                  );
                })}
              </div>

              <div className="cart-total">
                <span className="label">مجموع:</span>
                <span className="amount">{fmt(total)} تومان</span>
              </div>

              <div className="cart-checkout-action">
                {canShop ? (
                    <>
                      <Link to="/checkout" className="btn btn-primary">
                        {user ? 'ادامه جهت پرداخت' : 'ورود و ادامه جهت پرداخت'}
                      </Link>
                      {!user && (
                          <Link to="/signup" className="btn btn-outline">
                            ثبت‌نام
                          </Link>
                      )}
                    </>
                ) : (
                    <div className="cart-role-gate">
                      <p>
                        <i className="fas fa-info-circle" />
                        برای نهایی کردن خرید باید به عنوان <strong>مشتری</strong> ثبت‌نام کنید.
                      </p>
                      <div className="cart-role-gate-actions">
                        <Link to="/signup" className="btn btn-primary">
                          ثبت‌نام به عنوان مشتری
                        </Link>
                      </div>
                    </div>
                )}
              </div>
            </>
        )}
      </div>
  );
}